import { type DiscoverChatbotCardViewModel, formatDiscoverChatbotCardCount } from "@next-platform/client-common-core";
import { Icon, IconSize, IconVariants } from "@next-platform/client-common-spicy-ui";
import React from "react";

import { useTranslatedTags } from "@/hooks/useTranslatedTags";
import {
  DISCOVER_CHATBOT_CARD_DESCRIPTION_LINE_CLAMP_CLASS,
  DISCOVER_CHATBOT_CARD_DESCRIPTION_LINE_HEIGHT_PX,
  DISCOVER_CHATBOT_CARD_META_HEIGHT_PX,
} from "@/pages/Discover/components/discoverChatbotCardDimensions";

const META_STYLE = { height: DISCOVER_CHATBOT_CARD_META_HEIGHT_PX };
const DESCRIPTION_STYLE = { lineHeight: `${DISCOVER_CHATBOT_CARD_DESCRIPTION_LINE_HEIGHT_PX}px` };
const DESCRIPTION_CLASS_NAME = `m-0 ${DISCOVER_CHATBOT_CARD_DESCRIPTION_LINE_CLAMP_CLASS} text-[11px] text-gray-12`;
const LOREBOOK_BADGE_CLASS_NAME = "flex h-4 w-4 items-center justify-center rounded-[3px] bg-yellow-9";
const VOICE_BADGE_CLASS_NAME = "flex h-4 w-4 items-center justify-center rounded-[3px] bg-blue-10";
const TAG_CLASS_NAME = "flex h-4 items-center rounded-[3px] bg-gray-5 px-1 text-[10px] leading-4 whitespace-nowrap text-gray-12";
const STAT_CLASS_NAME = "mr-2 flex items-center text-[11px] text-gray-10";

type DiscoverChatbotCardBodyProps = {
  model: DiscoverChatbotCardViewModel;
  testId: string;
};

const DiscoverChatbotCardStatsRow = ({ model, testId }: DiscoverChatbotCardBodyProps) => {
  const { stats } = model;

  return (
    <div className="mt-1.5 flex items-center" data-testid={`${testId}-Stats`}>
      <span className={STAT_CLASS_NAME}>
        <Icon icon={IconVariants.BLOCKS} size={IconSize.XSMALL} className="mr-[3px] text-gray-10" />
        {formatDiscoverChatbotCardCount(stats.tokenCount)}
      </span>
      <span className={STAT_CLASS_NAME}>
        <Icon icon={IconVariants.MESSAGE} size={IconSize.XSMALL} className="mr-[3px] text-gray-10" />
        {formatDiscoverChatbotCardCount(stats.messageCount)}
      </span>
      <span className={STAT_CLASS_NAME}>
        <Icon icon={IconVariants.LIKE} size={IconSize.XSMALL} className="mr-[3px] text-gray-10" />
        {`${stats.ratingPercent}%`}
      </span>
    </div>
  );
};

const DiscoverChatbotCardBody = ({ model, testId }: DiscoverChatbotCardBodyProps) => {
  const translatedTags = useTranslatedTags(model.tags);
  const shouldShowTagsRow = model.hasLorebook || model.hasVoice || translatedTags.length > 0;

  return (
    <div className="flex flex-col px-2 pt-1 pb-2" data-testid={`${testId}-Body`}>
      <div className="flex flex-col overflow-hidden" style={META_STYLE} data-testid={`${testId}-Meta`}>
        {model.description ? (
          <p className={DESCRIPTION_CLASS_NAME} style={DESCRIPTION_STYLE}>
            {model.description}
          </p>
        ) : null}

        {shouldShowTagsRow ? (
          <div className="mt-auto flex max-h-4 flex-wrap items-start gap-x-1 gap-y-[3px] overflow-hidden" data-testid={`${testId}-Tags`}>
            {model.hasLorebook ? (
              <span className={LOREBOOK_BADGE_CLASS_NAME} data-testid={`${testId}-LorebookBadge`}>
                <Icon icon={IconVariants.BOOK} size={IconSize.XSMALL} className="text-white" />
              </span>
            ) : null}
            {model.hasVoice ? (
              <span className={VOICE_BADGE_CLASS_NAME} data-testid={`${testId}-VoiceBadge`}>
                <Icon icon={IconVariants.VOLUME_2} size={IconSize.XSMALL} className="text-white" />
              </span>
            ) : null}
            {translatedTags.map((translatedTag) => (
              <span key={translatedTag.original} className={TAG_CLASS_NAME}>
                {translatedTag.displayName}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <DiscoverChatbotCardStatsRow model={model} testId={testId} />
    </div>
  );
};

export default React.memo(DiscoverChatbotCardBody);
